import { useCallback, useEffect } from 'react';

import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { ModalProps } from '@/components/providers/ModalProvider/ModalProvider';
import { Button } from '@/components/shadcn/button';
import hasItems from '@/lib/utils/hasItems';

import type { CRM } from '../../types';

type Props = ModalProps & {
  onSubmit: (crm: CRM) => void;
  description?: string;
  availableCrms: Maybe<Array<{ key: string }>>;
};

export default function PickCRMModal({
  onSubmit,
  onClose,
  description,
  availableCrms,
}: Props) {
  const { t } = useTranslation();

  const handleSubmit = useCallback((crm: CRM) => onSubmit(crm), [onSubmit]);

  useEffect(() => {
    if (availableCrms?.length === 1) handleSubmit(availableCrms[0]?.key as CRM);
  }, [availableCrms, handleSubmit]);

  if (!hasItems(availableCrms)) {
    return (
      <div className="relative flex flex-col gap-4 p-4">
        <Button
          className="absolute top-2 right-2"
          variant="ghost"
          size="icon-xs"
          onClick={onClose}
        >
          <X />
        </Button>

        <div className="flex flex-col items-start">
          <p className="font-medium">{t('PICK_CRM_NONE_AVAILABLE_TITLE')}</p>

          <p className="text-muted-foreground text-sm">
            {t('PICK_CRM_NONE_AVAILABLE_DESCRIPTION')}
          </p>
        </div>

        <div className="flex w-full flex-col gap-1">
          <Button onClick={onClose} variant="secondary">
            {t('PICK_CRM_NONE_AVAILABLE_BUTTON')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col gap-4 p-4">
      <Button
        className="absolute top-2 right-2"
        variant="ghost"
        size="icon-xs"
        onClick={onClose}
      >
        <X />
      </Button>

      <div className="flex flex-col items-start">
        <p className="font-medium">{t('PICK_CRM_TITLE')}</p>

        <p className="text-muted-foreground text-center">
          {description ?? t('PICK_CRM_DESCRIPTION')}
        </p>
      </div>

      <div className="flex w-full flex-col gap-2">
        {availableCrms?.map((crm) => (
          <Button key={crm.key} onClick={() => handleSubmit(crm.key as CRM)}>
            {t('PICK_CRM_CONTINUE_WITH', {
              crm: t(`CRM_NAME_${crm.key.toUpperCase()}`),
            })}
          </Button>
        ))}
      </div>

      <div className="flex w-full flex-col gap-1">
        <Button onClick={onClose} variant="secondary">
          {t('PICK_CRM_CANCEL')}
        </Button>
      </div>
    </div>
  );
}
